import React, { useState, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Img } from 'react-image';
import Highlighter from 'react-highlight-words';
import { Trans, withI18n } from '@lingui/react';
import { Portal } from 'react-portal';
import format from 'date-fns/format';
import cx from 'classnames';
import find from 'lodash/find';

import LimitText from './utils/LimitText';
import { getAssetURL, getProductImageUrl } from '../api';
import noPhotoImg from '../images/no-photo.png';
import ArticleDetailsModal from './ArticleDetailsModal';
import { getLocaleFromLanguageCode } from '../intl-helpers';
import { getDetailValue } from '../utils/dataFormaters';
import { AddToCollectionBtn } from './AddToCollectionModal';
import { DeleteArticleBtn } from './DeleteArticleBtn';
import { connect } from 'react-redux';
import { useAssetPreview } from '../hooks/useAssetPreview';
import { selectors } from '../reducers/appReducer';

function isOdd(n) {
    return Math.abs(n % 2) === 1;
}

function getProperty(code, productProperties) {
    return productProperties[code];
}

function getPropertyValue(code, itemProps) {
    return find(itemProps, (prop) => prop.property === code);
}

const RowBlock = ({
                      index,
                      item,
                      onSelect,
                      q,
                      availableProps,
                      showLists,
                      deleteProductsGrant,
                      state,
                      language,
                      productProperties,
                      i18n,
                      priceDecimalPrecision,
                  }) => {
    const [showModal, setShowModal] = useState(false);

    const getPropertyDetailValue = (code) => {
        const locale = getLocaleFromLanguageCode(language);

        const result = getDetailValue(
            getPropertyValue(code, item.children),
            getProperty(code, productProperties),
            locale,
            productProperties,
            i18n,
            priceDecimalPrecision
        );

        if (!result) {
            return '-';
        }

        return result;
    };

    const getValiditaPrezzo = () => {
        if (item.prezzo_attuale && item.prezzo_attuale.data_inizio_validita_prezzo) {
            return format(item.prezzo_attuale.data_inizio_validita_prezzo.date, 'DD/MM/YYYY');
        }

        return null;
    };

    const openModal = () => {
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    const prezzo = getPropertyDetailValue('prezzo_listino');
    const validita_prezzo = getPropertyDetailValue('data_inizio_validita_prezzo');

    // Get dell' url dell'immagine
    const { url: itemImageUrl = null, publicUrl: itemImagePublicUrl = null } = getProductImageUrl(item) || {};

    // Aggiunto una variabile locale per gestire l' url dell' item costruito da getAssetUrl
    const assetItemImageUrl = getAssetURL(encodeURIComponent(itemImageUrl))

    // Get dell' url dell' image placeholder
    const imagePlaceholder = state ? selectors.getDefaultPlaceholderImage(state)?.imageUrl : null;
    const imagePlaceholderUrl = imagePlaceholder === './images/no-photo.png' ? noPhotoImg : (imagePlaceholder || noPhotoImg);

    // Get dell' anteprima dell' image tramite l' hook per il file singolo (da uniformare in unico hook)
    const shouldUsePreview = !itemImageUrl || !itemImageUrl.startsWith('http');
    const { previewImage, loading: imageLoading, error: imageError } = useAssetPreview(
        shouldUsePreview ? itemImagePublicUrl : null
    );

    // Url finale da usare in <Img>
    const finalImageUrl = itemImageUrl && itemImageUrl.startsWith('http')
        ? itemImageUrl
        : (assetItemImageUrl || itemImagePublicUrl)

    return (
        <Fragment>
            {showModal && (
                <Portal>
                    <ArticleDetailsModal
                        article={item}
                        onClose={closeModal}
                        containerId={`article-detail-modal-${item.id}`}
                    />
                </Portal>
            )}
            <div
                className={cx(
                    'card c-zoom-in rounded-sm col-span-2 lg:col-span-1',
                    isOdd(index) ? 'bg-alternate-row' : 'bg-white'
                )}
                onClick={openModal}
            >
                {(showLists || deleteProductsGrant) && (
                    <div
                        className="flex items-center ml-2.5 pt-0.5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <label
                            className="checkmark-container"
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelect(item.id);
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={item.isSelected}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => {}}
                            />
                            <span className="checkmark"></span>
                        </label>
                        {showLists && <AddToCollectionBtn itemId={item.id} />}
                        {deleteProductsGrant && (
                            <DeleteArticleBtn
                                code={getPropertyDetailValue('codice_articolo')}
                                id={item.id}
                            />
                        )}
                    </div>
                )}
                <div className="card-image-wrapper lg:flex pl-2.5 pr-2 pb-2 pt-0">
                    <div className="card-image lg:flex-grow">
                        <div
                            className="table-img-preview m-auto lg:m-0"
                            style={{ height: '130px' }}
                        >
                            <Img
                                src={finalImageUrl}
                                className="img-responsive m-a"
                                loader={
                                    <div className="rounded bg-secondary table-img-preview" />
                                }
                                // Fallback, uso unloader di Img
                                unloader={
                                    <img
                                        src={imagePlaceholderUrl}
                                        className="img-responsive m-a"
                                        style={{ maxHeight: '125px' }}
                                        alt="Placeholder"
                                    />
                                }
                                style={{
                                    maxHeight: '125px',
                                }}
                            />
                        </div>
                    </div>
                    <div className="card-image-side lg:flex-grow-0 pl-1 min-h-[4.5rem] hidden lg:flex">
                        <div>
                            <div className="text-label text-uppercase text-small">
                                {availableProps.includes('unita_ordine') && <Trans id="um" />}
                            </div>
                            <div
                                className="tooltip"
                                data-tooltip={getPropertyDetailValue('unita_ordine')}
                            >
                                {getPropertyDetailValue('unita_ordine')}
                            </div>
                            <hr className="hr-light" />
                            <div className="text-label text-uppercase text-small">
                                {availableProps.includes('unita_per_prezzo') && (
                                    <Trans id="conf" />
                                )}
                            </div>
                            {getPropertyDetailValue('unita_per_prezzo')}
                        </div>
                    </div>
                </div>
                <div className="card-header">
                    <div className="card-title h6">
                        <Highlighter
                            searchWords={[q]}
                            textToHighlight={getPropertyDetailValue('codice_articolo')}
                        />
                    </div>
                    <div className="card-subtitle">
                        <LimitText
                            limit={25}
                            highlight={q}
                            text={getPropertyDetailValue('descrizione_articolo')?.replace(
                                /(<([^>]+)>)/gi,
                                ''
                            )}
                        />
                    </div>
                </div>
                <div className="card-body">
                    <div className="card-body-wrapper">
                        <div className="text-label text-uppercase text-small">
                            {availableProps.includes('descrizione_marca') && (
                                <Trans id="brand" />
                            )}
                        </div>
                        <LimitText
                            limit={16}
                            text={getPropertyDetailValue('descrizione_marca')}
                        />
                        <div className="text-label text-uppercase text-small">
                            {availableProps.includes('serie') && <Trans id="series" />}
                        </div>
                        <LimitText limit={16} text={getPropertyDetailValue('serie')} />
                        <div className="text-label text-uppercase text-small">
                            {availableProps.includes('linee') && <Trans id="line" />}
                        </div>
                        <LimitText limit={16} text={getPropertyDetailValue('linea')} />
                        <div className="text-label text-uppercase text-small">
                            {availableProps.includes('modello') && <Trans id="model" />}
                        </div>
                        <LimitText limit={16} text={getPropertyDetailValue('modello')} />
                    </div>
                </div>
                <div className="card-footer">
                    <div className="lg:hidden">
                        <div className="text-label text-uppercase text-small">
                            {availableProps.includes('unita_ordine') && <Trans id="um" />}
                        </div>
                        <div
                            className="tooltip"
                            data-tooltip={getPropertyDetailValue('unita_ordine')}
                        >
                            {getPropertyDetailValue('unita_ordine')}
                        </div>
                        <hr className="hr-light" />
                        <div className="text-label text-uppercase text-small">
                            {availableProps.includes('unita_per_prezzo') && <Trans id="conf" />}
                        </div>
                        {getPropertyDetailValue('unita_per_prezzo')}
                    </div>
                    {prezzo !== null && prezzo !== '-' ? (
                        <Fragment>
                            <div className="table-price-lg">{`€ ${prezzo}`}</div>
                            <div className="table-date-sm text-error">
                                {validita_prezzo && validita_prezzo !== '-' && (
                                    <Fragment>
                                        <Trans id="from" /> {validita_prezzo}
                                    </Fragment>
                                )}
                            </div>
                        </Fragment>
                    ) : (
                        <>
                            <div className="table-price-lg" style={{ height: '53px' }}>
                                -
                            </div>
                            <div className="table-date-sm text-error"></div>
                        </>
                    )}
                </div>
            </div>
        </Fragment>
    );
};

RowBlock.propTypes = {
    bg: PropTypes.string,
    index: PropTypes.number,
    onCloseButtonClick: PropTypes.func,
    onSelect: PropTypes.func,
    showDetails: PropTypes.bool,
    showLists: PropTypes.bool,
    deleteProductsGrant: PropTypes.object,
    state: PropTypes.object,
    item: PropTypes.object,
    q: PropTypes.string,
    availableProps: PropTypes.array,
    language: PropTypes.string,
    productProperties: PropTypes.object,
    i18n: PropTypes.object,
    priceDecimalPrecision: PropTypes.number,
};

const mapStateToProps = (state) => ({
    state: state
});

export default connect(mapStateToProps)(withI18n()(RowBlock));